import type { ElementType, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Serif section heading — the only sanctioned serif below the PageHead H1
 * (design-grammar.md §2, "Serif placement"). Two levels:
 *
 *   - `title`      (20px)  — section H2
 *   - `card-title` (16px)  — card / dialog heading
 *
 * Replaces the ad-hoc serif `fontFamily` inline-style headings; body
 * copy, stat values, and button labels stay Inter/Mono.
 *
 * The primitive owns its line-height, the way `PageHead` owns the H1's.
 * The `title`/`card-title` fontSize tokens are bare strings in
 * `tailwind.config.js` (size only, no lineHeight tuple), so without this a
 * heading would inherit preflight's 1.5 — far too loose for display serif.
 * Call sites can still override with their own `leading-*` via `className`.
 */
interface SectionTitleProps {
  level?: 'title' | 'card-title';
  /** Rendered element; defaults to h2 for `title`, h3 for `card-title`. */
  as?: ElementType;
  /** For `aria-labelledby` from the section the heading names. */
  id?: string;
  className?: string;
  children: ReactNode;
}

export function SectionTitle({ level = 'title', as, id, className, children }: SectionTitleProps) {
  const Tag: ElementType = as ?? (level === 'title' ? 'h2' : 'h3');
  return (
    <Tag
      id={id}
      className={cn(
        'font-serif font-normal text-stevens-ink m-0',
        level === 'title' ? 'text-title leading-[1.2]' : 'text-card-title leading-[1.25]',
        className,
      )}
    >
      {children}
    </Tag>
  );
}
